import React, { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { useIsMobile } from 'src/hooks/useIsMobile';

export type NavbarContextType = {
  isOpen: boolean;
  isSecondaryDrawerOpen: boolean;
  closeNavbar: () => void;
  openNavbar: () => void;
  toggleNavbar?: () => void;
  setSecondaryDrawerOpen: (isOpen: boolean) => void;
};

export const NavbarContext = React.createContext<NavbarContextType | null>({
  isOpen: true,
  isSecondaryDrawerOpen: false,
  closeNavbar: () => {
    throw new Error('closeNavbar function not implemented');
  },
  openNavbar: () => {
    throw new Error('openNavbar function not implemented');
  },
  toggleNavbar: () => {
    throw new Error('toggleNavbar function not implemented');
  },
  setSecondaryDrawerOpen: () => {
    throw new Error('toggleSecondaryDrawer function not implemented');
  },
});

export const useNavbarContext = () => {
  const context = React.useContext(NavbarContext);
  if (!context) {
    throw new Error('useNavbarContext must be used within a NavbarProvider');
  }
  return context;
};

export const NavbarProvider = ({
  children,
  responsive = true,
}: {
  responsive?: boolean;
  children: React.ReactNode;
}) => {
  const isMobile = useIsMobile();
  const location = useLocation();

  useLayoutEffect(() => {
    if (responsive && isMobile) {
      setIsOpen(false);
    }
  }, [isMobile, location, responsive]);

  const [isOpen, setIsOpen] = React.useState(!isMobile);
  const [isSecondaryDrawerOpen, setIsSecondaryDrawerOpen] =
    React.useState(false);
  const closeNavbar = () => setIsOpen(false);
  const openNavbar = () => setIsOpen(true);

  const toggleNavbar = () => setIsOpen((prev) => !prev);

  const setSecondaryDrawerOpen = (isOpen: boolean) => {
    const isMobileNavbar = responsive && isMobile;
    if (isMobileNavbar) {
      isOpen ? closeNavbar() : openNavbar();
    }
    setIsSecondaryDrawerOpen(isOpen);
  };

  const value = {
    isOpen,
    isSecondaryDrawerOpen,
    closeNavbar,
    openNavbar,
    toggleNavbar,
    setSecondaryDrawerOpen,
  };

  return (
    <NavbarContext.Provider value={value}>{children}</NavbarContext.Provider>
  );
};
